import { useMemo, useState } from 'react';
import type { Currency, Interval, PublicRangeDto } from '@motoschool/shared';
import {
  BOOKING_START_STEP_MINUTES,
  LESSON_MINUTES_OPTIONS,
  MIN_LESSON_MINUTES,
  bookingStartOptions,
  fittingDurations,
  formatMoney,
  totalMinutes,
} from '@motoschool/shared';
import { Button, EmptyState, cn } from './ui';
import { lessonDurationLabel } from '../lib/useTeachers';
import {
  dayKeyOf,
  formatDayNumber,
  formatTime,
  fullDayLabel,
  pluralize,
  shortDayLabel,
} from '../lib/format';

/** Выбранное учеником время — с этим уходит заявка. */
export interface PickedLesson {
  startsAt: string;
  durationMinutes: number;
  teacherId: string;
  teacherName?: string;
  priceMinor: number | null;
}

/** Свободное и занятое время одного инструктора. */
interface TeacherTime {
  teacherId: string;
  teacherName?: string;
  hourlyRateMinor: number | null;
  free: PublicRangeDto[];
  busy: PublicRangeDto[];
}

interface PublicTimePickerProps {
  teachers: TeacherTime[];
  timeZone: string;
  currency: Currency;
  /** Какая длительность предложена первой — берётся из настроек школы. */
  defaultMinutes: number;
  onPick: (lesson: PickedLesson) => void;
}

/**
 * Высота минуты в календаре. Полчаса выходит в 45px — чуть больше минимальной
 * цели нажатия: строка получаса и есть кнопка выбора начала.
 */
const PX_PER_MINUTE = 1.5;

interface StartCell {
  key: string;
  startsAt: Date;
  durations: number[];
}

interface DayColumn {
  teacherId: string;
  teacherName?: string;
  hourlyRateMinor: number | null;
  free: Interval[];
  busy: Interval[];
  starts: StartCell[];
}

interface DayGroup {
  key: string;
  iso: string;
  freeMinutes: number;
  columns: DayColumn[];
  /** Границы оси времени: от первого события дня до последнего, по целым часам. */
  axisStart: Date;
  axisEnd: Date;
}

function toIntervals(ranges: PublicRangeDto[]): Interval[] {
  return ranges.map((range) => ({
    startsAt: new Date(range.startsAt),
    endsAt: new Date(range.endsAt),
  }));
}

/** Цена занятия по ставке инструктора — тот же расчёт, что на сервере. */
function priceFor(hourlyRateMinor: number | null, minutes: number): number | null {
  if (hourlyRateMinor === null) return null;
  return Math.round((hourlyRateMinor * minutes) / 60);
}

/**
 * Минуты от полуночи в поясе школы. Считаются из отформатированного времени,
 * а не из UTC: у пояса бывает смещение в полчаса, и тогда округление оси по
 * меткам UTC поставило бы линии часов посреди строк.
 */
function localMinutes(date: Date, timeZone: string): number {
  const [hours, minutes] = formatTime(date.toISOString(), timeZone).split(':').map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

const minutesBetween = (from: Date, to: Date): number => (to.getTime() - from.getTime()) / 60_000;

/**
 * Выбор времени занятия — календарь дня.
 *
 * Кнопками со временем это уже было, и по ним не читалось главное: где день
 * открыт подряд, а где разорван занятием. На оси времени это видно сразу —
 * белые полосы свободны, заштрихованные заняты, серое вне часов работы.
 * Начало выбирается с шагом в полчаса, длительность — отдельно, и выбранное
 * занятие рисуется на той же оси: видно, до скольки оно идёт.
 */
export function PublicTimePicker({
  teachers,
  timeZone,
  currency,
  defaultMinutes,
  onPick,
}: PublicTimePickerProps): React.JSX.Element {
  const [pickedDay, setPickedDay] = useState<string | null>(null);
  const [pickedStart, setPickedStart] = useState<string | null>(null);
  const [pickedMinutes, setPickedMinutes] = useState<number | null>(null);

  const days = useMemo<DayGroup[]>(() => {
    // Дни задаёт свободное время: в день без единого свободного часа
    // ученику заходить незачем.
    const keys = new Map<string, string>();
    for (const teacher of teachers) {
      for (const range of teacher.free) {
        const key = dayKeyOf(range.startsAt, timeZone);
        if (!keys.has(key)) keys.set(key, range.startsAt);
      }
    }

    return [...keys.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([key, iso]) => {
        const inDay = (range: Interval) =>
          dayKeyOf(range.startsAt.toISOString(), timeZone) === key;

        const columns: DayColumn[] = teachers.map((teacher) => {
          const free = toIntervals(teacher.free).filter(inDay);

          return {
            teacherId: teacher.teacherId,
            teacherName: teacher.teacherName,
            hourlyRateMinor: teacher.hourlyRateMinor,
            free,
            busy: toIntervals(teacher.busy).filter(inDay),
            starts: bookingStartOptions({
              free,
              minMinutes: MIN_LESSON_MINUTES,
              stepMinutes: BOOKING_START_STEP_MINUTES,
            }).map((startsAt) => ({
              key: `${teacher.teacherId}:${startsAt.toISOString()}`,
              startsAt,
              durations: fittingDurations(free, startsAt, LESSON_MINUTES_OPTIONS),
            })),
          };
        });

        const moments = columns.flatMap((column) => [...column.free, ...column.busy]);
        const first = new Date(Math.min(...moments.map((item) => item.startsAt.getTime())));
        const last = new Date(Math.max(...moments.map((item) => item.endsAt.getTime())));

        // Ось начинается и кончается на целом часе — иначе метки часов
        // не совпадут с линиями сетки.
        const tail = localMinutes(last, timeZone) % 60;

        return {
          key,
          iso,
          freeMinutes: columns.reduce((sum, column) => sum + totalMinutes(column.free), 0),
          columns,
          axisStart: new Date(first.getTime() - (localMinutes(first, timeZone) % 60) * 60_000),
          axisEnd: new Date(last.getTime() + (tail === 0 ? 0 : (60 - tail) * 60_000)),
        };
      });
  }, [teachers, timeZone]);

  /*
   * Выбор храним ключами, а не индексами: при смене инструктора список дней
   * и времён другой, и по несуществующему ключу мы честно откатываемся
   * к первому дню вместо чужого времени под тем же номером.
   */
  const activeDay = days.find((day) => day.key === pickedDay) ?? days[0];

  const activeStart = activeDay?.columns
    .flatMap((column) => column.starts.map((start) => ({ column, start })))
    .find((item) => item.start.key === pickedStart);

  const durations = activeStart?.start.durations ?? [];
  const activeMinutes =
    pickedMinutes !== null && durations.includes(pickedMinutes)
      ? pickedMinutes
      : (durations.find((minutes) => minutes === defaultMinutes) ?? durations[0]);

  if (!activeDay) {
    return (
      <EmptyState
        title="Свободного времени нет"
        description="Загляните позже или напишите инструктору."
      />
    );
  }

  const axisMinutes = minutesBetween(activeDay.axisStart, activeDay.axisEnd);
  const trackHeight = axisMinutes * PX_PER_MINUTE;
  const withNames = activeDay.columns.some((column) => column.teacherName !== undefined);

  /** Отступ сверху в пикселях для момента времени. */
  const top = (date: Date): number => minutesBetween(activeDay.axisStart, date) * PX_PER_MINUTE;
  const height = (from: Date, to: Date): number => minutesBetween(from, to) * PX_PER_MINUTE;

  const hourLines: Date[] = [];
  for (let minute = 0; minute <= axisMinutes; minute += 60) {
    hourLines.push(new Date(activeDay.axisStart.getTime() + minute * 60_000));
  }

  const selectionEnd =
    activeStart && activeMinutes !== undefined
      ? new Date(activeStart.start.startsAt.getTime() + activeMinutes * 60_000)
      : null;
  const price =
    activeStart && activeMinutes !== undefined
      ? priceFor(activeStart.column.hourlyRateMinor, activeMinutes)
      : null;

  return (
    <div className="space-y-4">
      {/* Полоса дней выходит за поля страницы, чтобы край экрана подсказывал прокрутку */}
      <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 py-0.5">
        {days.map((day) => {
          const isActive = day.key === activeDay.key;
          const hours = Math.round(day.freeMinutes / 60);

          return (
            <button
              key={day.key}
              type="button"
              aria-pressed={isActive}
              onClick={() => {
                setPickedDay(day.key);
                setPickedStart(null);
              }}
              className={cn(
                'flex w-20 shrink-0 flex-col items-center gap-0.5 rounded-xl border px-2 py-2 transition-colors',
                isActive
                  ? 'border-night bg-night text-white'
                  : 'border-line bg-surface hover:border-brand-500 hover:bg-brand-50',
              )}
            >
              <span className="text-xs font-medium lowercase">
                {shortDayLabel(day.iso, timeZone)}
              </span>
              <span className="text-lg font-semibold leading-tight tabular-nums">
                {formatDayNumber(day.iso, timeZone)}
              </span>
              <span className={cn('text-xs tabular-nums', isActive ? 'text-accent' : 'text-muted')}>
                {`${hours} ${pluralize(hours, 'час', 'часа', 'часов')}`}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 className="text-sm font-semibold">{fullDayLabel(activeDay.iso, timeZone)}</h3>
        <p className="flex items-center gap-3 text-xs text-muted">
          <span className="flex items-center gap-1.5">
            <span className="size-3 rounded-sm border border-line bg-surface" aria-hidden />
            свободно
          </span>
          <span className="flex items-center gap-1.5">
            <span className="busy-hatch size-3 rounded-sm border border-line" aria-hidden />
            занято
          </span>
        </p>
      </div>

      {/* ------------------------------------------------------- календарь дня */}
      <div className="no-scrollbar -mx-4 overflow-x-auto px-4 pt-2">
        <div className="flex min-w-full gap-1">
          {/*
            Часы прилипают к левому краю: при нескольких инструкторах колонки
            уезжают вбок, а ось времени должна оставаться на виду.
          */}
          <div className="sticky left-0 z-20 w-12 shrink-0 bg-canvas pr-2">
            {withNames && <div className="h-8" />}
            <div className="relative" style={{ height: trackHeight }}>
              {hourLines.slice(0, -1).map((line) => (
                <span
                  key={line.toISOString()}
                  className="absolute inset-x-0 -translate-y-1/2 text-right text-xs tabular-nums text-muted"
                  style={{ top: top(line) }}
                >
                  {formatTime(line.toISOString(), timeZone)}
                </span>
              ))}
            </div>
          </div>

          {activeDay.columns.map((column) => (
            <div key={column.teacherId} className="min-w-36 flex-1">
              {withNames && (
                <p className="flex h-8 items-center truncate text-xs font-semibold">
                  {column.teacherName}
                </p>
              )}

              <div
                className="relative overflow-hidden rounded-xl bg-canvas ring-1 ring-line"
                style={{ height: trackHeight }}
              >
                {column.busy.map((range) => (
                  <div
                    key={range.startsAt.toISOString()}
                    className="busy-hatch absolute inset-x-0 overflow-hidden border-y border-line px-2 pt-0.5"
                    style={{
                      top: top(range.startsAt),
                      height: height(range.startsAt, range.endsAt),
                    }}
                  >
                    <span className="text-xs text-muted">занято</span>
                  </div>
                ))}

                {/* Свободные полосы белые: на сером фоне нерабочего времени
                    сразу видно, где день вообще открыт */}
                {column.free.map((range) => (
                  <div
                    key={range.startsAt.toISOString()}
                    className="absolute inset-x-0 rounded-lg bg-surface ring-1 ring-line"
                    style={{
                      top: top(range.startsAt),
                      height: height(range.startsAt, range.endsAt),
                    }}
                    aria-hidden
                  />
                ))}

                {/*
                  Линии часов рисуются поверх полос: под ними их закрыла бы
                  белая заливка свободного времени, и сетка пропала бы именно
                  там, где она нужнее всего.
                */}
                {hourLines.map((line) => (
                  <span
                    key={line.toISOString()}
                    className="pointer-events-none absolute inset-x-0 border-t border-line"
                    style={{ top: top(line) }}
                    aria-hidden
                  />
                ))}

                {column.starts.map((start) => {
                  /*
                   * Целые часы подписаны в колонке слева, поэтому здесь стоят
                   * только получасы — иначе одно и то же время читалось бы
                   * дважды в двух сантиметрах друг от друга. Пунктир заодно
                   * показывает, что начать можно и в 14:30.
                   */
                  const isHalfHour = localMinutes(start.startsAt, timeZone) % 60 !== 0;

                  return (
                    <button
                      key={start.key}
                      type="button"
                      aria-pressed={start.key === pickedStart}
                      // Целые часы подписаны только слева, поэтому у кнопки
                      // должно быть своё имя — иначе она пустая для читалки.
                      aria-label={`Начать в ${formatTime(start.startsAt.toISOString(), timeZone)}`}
                      onClick={() => {
                        setPickedStart(start.key);
                        setPickedMinutes(null);
                      }}
                      className={cn(
                        'absolute inset-x-0 flex items-start px-2 pt-0.5 text-left text-xs tabular-nums text-muted transition-colors hover:bg-brand-50',
                        isHalfHour && 'border-t border-dashed border-line',
                      )}
                      style={{
                        top: top(start.startsAt),
                        height: BOOKING_START_STEP_MINUTES * PX_PER_MINUTE,
                      }}
                    >
                      {isHalfHour ? formatTime(start.startsAt.toISOString(), timeZone) : ''}
                    </button>
                  );
                })}

                {/* Выбранное занятие поверх сетки. Клики пропускает насквозь —
                    иначе внутри длинного занятия не выбрать другое начало */}
                {activeStart?.column.teacherId === column.teacherId && selectionEnd && (
                  <div
                    className="pointer-events-none absolute inset-x-0 overflow-hidden rounded-lg border-l-4 border-accent bg-night px-2 py-1 text-white"
                    style={{
                      top: top(activeStart.start.startsAt),
                      height: height(activeStart.start.startsAt, selectionEnd),
                    }}
                  >
                    <p className="text-xs font-semibold tabular-nums">
                      {formatTime(activeStart.start.startsAt.toISOString(), timeZone)}
                      {' — '}
                      {formatTime(selectionEnd.toISOString(), timeZone)}
                    </p>
                    <p className="text-xs text-white/70">Ваше занятие</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ------------------------------------------------- длительность и заявка */}
      {activeStart === undefined || activeMinutes === undefined || selectionEnd === null ? (
        <p className="rounded-card border border-dashed border-line px-4 py-3 text-center text-sm text-muted">
          Нажмите на время, с которого хотите начать
        </p>
      ) : (
        /* Панель прилипает к низу экрана: календарь длинный, и без неё после
           выбора времени пришлось бы листать вниз за кнопкой */
        <div className="safe-bottom sticky bottom-0 -mx-4 space-y-3 border-t border-line bg-surface px-4 pt-3">
          <div>
            <p className="text-sm font-medium">Сколько часов</p>
            <div className="no-scrollbar -mx-4 mt-2 flex gap-2 overflow-x-auto px-4">
              {durations.map((minutes) => (
                <button
                  key={minutes}
                  type="button"
                  aria-pressed={minutes === activeMinutes}
                  onClick={() => setPickedMinutes(minutes)}
                  className={cn(
                    'min-h-11 min-w-16 shrink-0 rounded-xl border px-3 text-sm font-semibold transition-colors',
                    minutes === activeMinutes
                      ? 'border-night bg-night text-white'
                      : 'border-line bg-surface hover:border-brand-500 hover:bg-brand-50',
                  )}
                >
                  {lessonDurationLabel(minutes)}
                </button>
              ))}
            </div>
          </div>

          <p className="text-sm text-muted">
            <b className="text-ink tabular-nums">
              {formatTime(activeStart.start.startsAt.toISOString(), timeZone)}
              {' — '}
              {formatTime(selectionEnd.toISOString(), timeZone)}
            </b>
            {activeStart.column.teacherName ? ` · ${activeStart.column.teacherName}` : ''}
            {price === null ? '' : ` · ${formatMoney(price, currency)}`}
          </p>

          <Button
            fullWidth
            onClick={() =>
              onPick({
                startsAt: activeStart.start.startsAt.toISOString(),
                durationMinutes: activeMinutes,
                teacherId: activeStart.column.teacherId,
                teacherName: activeStart.column.teacherName,
                priceMinor: price,
              })
            }
          >
            Записаться
          </Button>
        </div>
      )}
    </div>
  );
}
